import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check, CreditCard, LogOut } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ServerBillingStatus, ServerMembership } from '@oracord/shared';
import { useSessionStore } from '../stores/session';

// Dropdown in the header. Lists the user's real memberships (from
// /api/me), lets them switch, and keeps account-level actions together.
// Never shows an org the user isn't a member of — the list IS the set
// of things the server will authorize them against.
export function OrgSwitcher({
  memberships,
  billing,
  canViewBilling,
  onSignOut,
}: {
  memberships: ServerMembership[];
  billing: ServerBillingStatus | null;
  canViewBilling: boolean;
  onSignOut: () => void;
}) {
  const activeId = useSessionStore(s => s.activeOrganizationId);
  const setActive = useSessionStore(s => s.setActiveOrganizationId);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const active = memberships.find(m => m.id === activeId) ?? memberships[0];

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!active) return null;

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm hover:bg-slate-50"
      >
        <span className="font-medium text-slate-900">{active.name}</span>
        <span className="text-xs text-slate-500 capitalize">{active.role}</span>
        <ChevronDown className="h-4 w-4 text-slate-400" />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-64 rounded-md border border-slate-200 bg-white shadow-lg z-10">
          <div className="py-1">
            {memberships.map(m => {
              const isActive = m.id === active.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => {
                    setActive(m.id);
                    setOpen(false);
                  }}
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
                >
                  <div>
                    <div className="font-medium text-slate-900">{m.name}</div>
                    <div className="text-xs text-slate-500 capitalize">{m.role}</div>
                  </div>
                  {isActive && <Check className="h-4 w-4 text-blue-600" />}
                </button>
              );
            })}
          </div>
          {canViewBilling && (
            <div className="border-t border-slate-100 py-1">
              <Link
                to="/billing"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                <CreditCard className="h-4 w-4" />
                <span className="flex-1">Billing</span>
                {billing && (
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${billing.active ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                    {billing.active ? `${formatPlanName(billing.plan)} active` : 'Inactive'}
                  </span>
                )}
              </Link>
            </div>
          )}
          <div className="border-t border-slate-100 py-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                void onSignOut();
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function formatPlanName(plan: string) {
  if (!plan) return 'Plan';
  return plan
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(part => part.slice(0, 1).toUpperCase() + part.slice(1))
    .join(' ');
}
